import React, { useCallback, useState } from "react";
import { useDropzone } from "react-dropzone";
import classNames from "classnames";
import "./ReceiptUpload.scss";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { ChildrenProps } from "../../../types";
import { ReceiptUploadContextProvider } from "./receiptUpload.context";

const MAX_RECEIPT_SIZE_MB = 10;
const MAX_RECEIPT_SIZE_BYTES = MAX_RECEIPT_SIZE_MB * 1024 * 1024;

const ERROR_CODES: Record<string, string> = {
    "file-too-large": `Max allowed size is ${MAX_RECEIPT_SIZE_MB}MB`,
    "file-invalid-type": "Only PDF, PNG, JPEG files allowed",
};

interface ReceiptUploadDropzoneProps extends ChildrenProps {
    onAddReceipt(file: File): Promise<void>;
    containerClassName?: string;
}

export const ReceiptUploadDropzone: React.FC<ReceiptUploadDropzoneProps> = ({
    onAddReceipt,
    containerClassName,
    children,
}) => {
    const [uploading, setUploading] = useState<boolean>(false);
    const { error: errorToast } = useToaster();

    const onError = useCallback(
        (error: string) => {
            errorToast(ERROR_CODES[error] ?? "Failed to upload file");
        },
        [errorToast],
    );

    const handleAddReceipt = useCallback(
        async (file: File) => {
            const addReceipt = async () => {
                setUploading(true);
                await onAddReceipt(file);
            };
            await addReceipt().finally(() => {
                setUploading(false);
            });
        },
        [onAddReceipt],
    );

    const { getRootProps, getInputProps, isDragAccept, open } = useDropzone({
        onDropRejected: ([rejection]) => onError(rejection.errors[0].code),
        onDropAccepted: async ([file]) => {
            await handleAddReceipt(file);
        },
        accept: ["image/png", "image/jpeg", "application/pdf"],
        maxSize: MAX_RECEIPT_SIZE_BYTES,
        multiple: false,
        noClick: true,
    });

    return (
        <ReceiptUploadContextProvider
            value={{
                isUploading: uploading,
                inputProps: getInputProps(),
                isDragActive: isDragAccept,
                open,
            }}
        >
            <div
                {...getRootProps({
                    className: classNames(
                        "receipt-upload__dropzone",
                        {
                            "receipt-upload__dropzone--active": isDragAccept,
                        },
                        containerClassName,
                    ),
                })}
            >
                {children}
            </div>
        </ReceiptUploadContextProvider>
    );
};
